export const MATCH_SERVICE_TYPES = ['Analyst', 'Assistant'] as const;
export type MatchServiceType = typeof MATCH_SERVICE_TYPES[number];

/**
 * Roles that can pick up Assessment postings. Assessment postings never come
 * back from the match procs, so the Openings widget shows these roles a second
 * table of open assessment postings alongside their matches.
 */
export const ANALYST_ROLES = [
    'Analyst',
    'Senior Analyst',
    'Senior Analyst and Training Coordinator',
    'Regional Clinical Training Coordinator',
    'Regional Coordinator',
    'Program Manager',
] as const;

export const isAnalystRole = (role: string | null | undefined): boolean =>
    !!role && ANALYST_ROLES.some(r => r.toLowerCase() === role.toLowerCase());

export const openStaffingGroupByReferenceId = (referenceId: string) => {
    if (window.openStaffingGroupDirectlyWithReferenceId) {
        window.openStaffingGroupDirectlyWithReferenceId(referenceId);
    }
};

/**
 * SharePoint lookup fields come across with their list item ids attached, either
 * bracketed ("[105;#Medicaid],[41;#Other]") or as the classic delimited form
 * ("105;#Medicaid;#41;#Other"). Returns just the names: "Medicaid, Other".
 * Values without an id delimiter are returned untouched.
 */
export function formatLookupValues(value: string | null | undefined): string {
    if (!value) return '';
    if (!value.includes(';#')) return value.trim();

    const bracketed = value.match(/\[[^\]]*\]/g);
    const names = bracketed
        // Bracketed form: one lookup per pair of brackets, name after the ';#'.
        ? bracketed.map(part => part.slice(1, -1).split(';#').slice(1).join(';#'))
        // Delimited form: alternating id / name.
        : value.split(';#').filter((_, i) => i % 2 === 1);

    return names.map(name => name.trim()).filter(Boolean).join(', ');
}

export interface ClientFilters {
    name: string;
    statuses: string[];
    enrollmentStatuses: string[];
    insuranceCompanies: string[];
    cities: string[];
}

export const DEFAULT_CLIENT_STATUSES = ['Active', 'Potential', 'Waitlist'];

export function getClientStatusBadgeColor(status: string | null): string {
    switch (status) {
        case 'Potential':
        case 'Waitlist': return 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100';
        case 'Active': return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100';
        case 'Inactive': return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-100';
        default: return 'bg-muted text-muted-foreground hover:bg-muted';
    }
}

export const CLIENT_STATUS_OPTIONS = [
    'Active',
    'Inactive',
    'Potential',
    'Denied',
    'Waitlist',
    'OnHold',
    'Wait List Overflow',
].map(s => ({ label: s, value: s, badgeClassName: getClientStatusBadgeColor(s) }));

export const ENROLLMENT_STATUS_OPTIONS = [
    'Clear- No Flag',
    'Green-Ready to staff',
    'Red-Waiting on documentation',
    'Blue- Submitted Tx plan waiting on auth',
    'Consult Only',
    'Crisis Committee',
    'Critical Staffing',
    'Discharging',
    'Duplicate File',
    'Fading services',
    'In Appeal',
    'In Assessment',
    'Insurance Change',
    'Needs Assistant',
    'Parent calling us back',
    'Requested Assessment Auth',
    'Telehealth',
    'Waiting on Credentialing',
].map(s => ({ label: s, value: s }));

export interface ApplicantFilters {
    name: string;
    statuses: string[];
    roles: string[];
    cities: string[];
}

export const DEFAULT_APPLICANT_STATUSES = [
    'In Interviews',
    'Pending Hire',
    'Potential',
    'Hire in Process',
    'Pre-Hire Documents Completed',
    'Hire Pending',
    'Requested Pre-Hire Documents',
];

export function getApplicantStatusBadgeColor(status: string): string {
    switch (status) {
        case 'In Interviews':
        case 'Hire in Process':
            return 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100';
        case 'Pre-Hire Documents Completed':
            return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100';
        case 'Pending Hire':
        case 'Hire Pending':
            return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100';
        case 'Requested Pre-Hire Documents':
            return 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100';
        default:
            return 'bg-muted text-muted-foreground border hover:bg-muted';
    }
}

export const APPLICANT_STATUS_OPTIONS = DEFAULT_APPLICANT_STATUSES.map(s => ({
    label: s,
    value: s,
    badgeClassName: getApplicantStatusBadgeColor(s),
}));

export const APPLICANT_ROLE_OPTIONS = [
    'Analyst',
    'Assistant',
    'Program Manager',
    'Regional Clinical Training Coordinator',
    'Regional Coordinator',
    'Registered Behavior Technician',
    'School Contract Supervisor',
    'Senior Analyst',
    'Student Analyst',
].map(s => ({ label: s, value: s }));

export interface StaffFilters {
    name: string;
    roles: string[];
    empStatuses: string[];
    cities: string[];
}

export const STAFF_ROLE_OPTIONS = [
    'Analyst',
    'Assistant',
    'Program Manager',
    'Regional Clinical Training Coordinator',
    'Regional Coordinator',
    'Registered Behavior Technician',
    'School Contract Supervisor',
    'Senior Analyst',
    'Senior Analyst and Training Coordinator',
    'Student Analyst',
].map(s => ({ label: s, value: s }));

export function getStaffEmpStatusBadgeColor(status: string): string {
    switch (status) {
        case 'Exempt': return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100';
        case 'Non-Exempt': return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100';
        default: return 'bg-muted text-muted-foreground border hover:bg-muted';
    }
}

export const STAFF_EMP_STATUS_OPTIONS = [
    { label: 'Exempt', value: 'Exempt', badgeClassName: getStaffEmpStatusBadgeColor('Exempt') },
    { label: 'Non-Exempt', value: 'Non-Exempt', badgeClassName: getStaffEmpStatusBadgeColor('Non-Exempt') },
];

export function buildStaffFilter(region: string, filters: StaffFilters): string {
    const parts: string[] = ['Active eq true'];
    if (region) parts.push(`Region eq '${region}'`);
    if (filters.roles.length > 0) {
        parts.push(`Role in (${filters.roles.map(s => `'${s}'`).join(', ')})`);
    }
    if (filters.empStatuses.length > 0) {
        parts.push(`EmpStatus in (${filters.empStatuses.map(s => `'${s}'`).join(', ')})`);
    }
    if (filters.cities.length > 0) {
        parts.push(`City in (${filters.cities.map(s => `'${s}'`).join(', ')})`);
    }
    if (filters.name.trim()) parts.push(`contains(tolower(FullName), '${filters.name.toLowerCase().trim()}')`);
    return parts.join(' and ');
}

export function buildApplicantFilter(region: string, filters: ApplicantFilters): string {
    const parts: string[] = [];
    if (region) parts.push(`Region eq '${region}'`);
    if (filters.statuses.length > 0) {
        parts.push(`Status in (${filters.statuses.map(s => `'${s}'`).join(', ')})`);
    }
    if (filters.roles.length > 0) {
        parts.push(`Role in (${filters.roles.map(s => `'Applicant ${s}'`).join(', ')})`);
    }
    if (filters.cities.length > 0) {
        parts.push(`City in (${filters.cities.map(s => `'${s}'`).join(', ')})`);
    }
    parts.push('LastPortalLogin ne null');
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    parts.push(`LastPortalLogin ge ${ninetyDaysAgo.toISOString()}`);
    if (filters.name.trim()) parts.push(`contains(tolower(FullName), '${filters.name.toLowerCase().trim()}')`);
    return parts.join(' and ');
}

export function buildClientFilter(region: string, filters: ClientFilters): string {
    const parts: string[] = [`Region eq '${region}'`];
    if (filters.statuses.length > 0) {
        parts.push(`ClientStatus in (${filters.statuses.map(s => `'${s}'`).join(', ')})`);
    }
    if (filters.enrollmentStatuses.length > 0) {
        parts.push(`EnrollmentStatus in (${filters.enrollmentStatuses.map(s => `'${s}'`).join(', ')})`);
    }
    if (filters.insuranceCompanies.length > 0) {
        parts.push(`InsuranceCompany in (${filters.insuranceCompanies.map(s => `'${s}'`).join(', ')})`);
    }
    if (filters.cities.length > 0) {
        parts.push(`ParentCaregiver1City in (${filters.cities.map(s => `'${s}'`).join(', ')})`);
    }
    if (filters.name.trim()) {
        parts.push(`contains(tolower(NameFL), '${filters.name.toLowerCase().trim()}')`);
    }
    return parts.join(' and ');
}

export interface PostingFilters {
    statuses: string[];
    clientIds: number[];
    serviceRequestedByOnly: boolean;
    /** Staffing reason ids (the `ReasonId` values in a posting's Reasons JSON). Empty = no reason filter. */
    reasonIds: number[];
}

export const DEFAULT_POSTING_STATUSES = ['Active'];

export const DEFAULT_POSTING_FILTERS: PostingFilters = {
    statuses: DEFAULT_POSTING_STATUSES,
    clientIds: [],
    serviceRequestedByOnly: false,
    reasonIds: []
};

/**
 * The substring that identifies one reason inside a posting's `Reasons` JSON column.
 * `Reasons` is written by `staffing.pCreateNewPosting(Bulk)` with `FOR JSON PATH`, so each entry is
 * compact and starts `{"ReasonId":7,"Reason":...}`. Matching `"ReasonId":7,` (trailing comma included)
 * keeps 7 from matching 70; matching on the id rather than the title survives title edits on the
 * SharePoint list and titles that are substrings of one another.
 */
export function reasonIdToken(reasonId: number): string {
    return `"ReasonId":${reasonId},`;
}

export function buildPostingFilter(region: string, view: string, filters: PostingFilters): string {
    let filter = `region eq '${region}' ${getServiceTypeFilterFromView(view)}`;
    if (filters.statuses.length > 0) {
        filter += ` and Status in (${filters.statuses.map(s => `'${s}'`).join(', ')})`;
    }
    if (filters.clientIds.length > 0) {
        filter += ` and ClientId in (${filters.clientIds.join(', ')})`;
    }
    if (view === 'service_request' || filters.serviceRequestedByOnly) {
        filter += ` and ServiceRequestedBy ne null`;
    }
    if (filters.reasonIds.length > 0) {
        // OR across the selected reasons: a posting matches when it carries ANY of them.
        // Server-side (the grid is paged by OData), so it runs over the whole posting set.
        // Postings with null Reasons (manually created) never match — correct.
        const clauses = filters.reasonIds.map(id => `contains(Reasons, '${reasonIdToken(id)}')`);
        filter += ` and (${clauses.join(' or ')})`;
    }
    return filter;
}

export function getStatusBadgeColor(status: string): string {
    switch (status) {
        case 'Active': return 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100';
        case 'Staffed': return 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100';
        case 'Staffed and Billed': return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100';
        case 'Closed': return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-100';
        default: return 'bg-muted text-muted-foreground hover:bg-muted';
    }
}
export const POSTING_STATUS_OPTIONS = ['Active', 'Staffed', 'Staffed and Billed', 'Closed'].map(s => ({
    label: s,
    value: s,
    badgeClassName: getStatusBadgeColor(s),
}));

const ASP_STEP_LABELS: Record<string, string> = {
    NoPreAuthAssessment: 'Assessment Auth Created (No Auth Present and PreAuth not Required)',
    AutoCredentialing: 'Auto Credentialing',
    RBTCertificationRequirement: 'RBT Certification Requirement',
    FortyHourCertificationRequirement: '40 Hr. Certification Requirment',
    AddToASP: 'Create ASP Items',
};

export function getAspStepLabel(step: string): string {
    return ASP_STEP_LABELS[step] ?? step;
}

export function getAspStepStatusBadgeColor(status: string): string {
    switch (status) {
        case 'Success': return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100';
        case 'Skipped': return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100';
        case 'Error': return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-100';
        default: return 'bg-muted text-muted-foreground hover:bg-muted';
    }
}

export function getViewFromServiceType(serviceType: string): 'analyst' | 'assistant_rbt' | 'assessment' {
    switch (serviceType) {
        case 'Analyst':
        case 'Program Manager':
            return 'analyst';
        case 'Registered Behavior Technician':
        case 'Student Analyst':
        case 'Assistant':
            return 'assistant_rbt';
        case 'Assessment':
            return 'assessment';
        default:
            return 'analyst';
    }
}
export function getServiceTypeFilterFromView(view: string): string {
    switch (view) {
        case 'analyst':
            return "and ServiceType eq 'Analyst'";
        case 'assistant_rbt':
            return "and ServiceType eq 'Assistant'";
        case 'assessment':
            return "and ServiceType eq 'Assessment'"
        case 'service_request':
            return '';
        default:
            return '';
    }
}
export function buildClientCityLookupFilter(region: string, filters: ClientFilters): string {
    return buildClientFilter(region, { ...filters, cities: [], name: '' });
}

export function buildStaffCityLookupFilter(region: string, filters: StaffFilters): string {
    return buildStaffFilter(region, { ...filters, cities: [], name: '' });
}

export function buildApplicantCityLookupFilter(region: string, filters: ApplicantFilters): string {
    return buildApplicantFilter(region, { ...filters, cities: [], name: '' });
}

export function getNumProcessesColor(num: number): string {
    if (num > 3) return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-100 hover:text-green-800';
    if (num >= 1) return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100 hover:text-yellow-800';
    return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-100 hover:text-red-800';
}